import "./EditableGridCell.css";
import * as React from "react";
import { connect } from "react-redux";
import { DisplayFieldService } from "../DisplayFieldService";
import { Icon, IconButton } from "office-ui-fabric-react";
import { IBaseEntity } from "../../../entities/common";
import { IListViewFieldColumn, ViewService } from "../../../services/ViewService";
import { isLockedField } from "../../utils/common";
import { GridCellProps } from "./EditableGridCell";
import { ApplicationState } from "../../../store";
import { TenantState } from "../../../store/Tenant";
import { UserState } from "../../../store/User";
import * as Metadata from "../../../entities/Metadata";
import { EntityType } from "../../../entities/common";
import { getTimephasedInlineEditState } from "../../../services/TimephasedValueService";
import { getDefaultCostRateTable, IWithDefaultCostRateTable } from "../../../store/selectors/costRateTableSelectors";
import { FieldsState } from "../../../store/fields";
import { resolveTimephasedFieldFromStore } from "../../../services/TimephasedConfigService";
import { getTimephasedConfig } from "../../resource/TimephasedDataPanel/TimephasedDataPanel";
import { Resource } from "../../../store/ResourcesListStore";

type StateProps = {
    user: UserState;
    tenant: TenantState;
    fieldsStore?: Record<string, FieldsState>;
} & IWithDefaultCostRateTable;

type Props<T extends IBaseEntity> = GridCellProps<T> & StateProps & {
    onEditClick: () => void,
    value: any,
    controlId?: string,
};

export const isReadonlyCell = (props: GridCellProps<IBaseEntity> & StateProps): string | undefined => {
    const readonlyCustomControl = props.customFieldElementRender?.[props.field.name]?.({}, props.item, props.field, undefined) === null;

    const isReadonlyByDefault = props.field.isReadonly || !!props.field.isFake || !!props.readonly || readonlyCustomControl
        || isLockedField(props.field.settings?.format, props.field.name, props.user, props.tenant, props.entityType);

    if (isReadonlyByDefault) {
        return "This cell is read-only";
    }

    if (props.entityType === EntityType.Resource && props.field.type === Metadata.FieldType.TimephasedValue) {
        const { isInlineEditable } = getTimephasedInlineEditState(props.field.name, props.item as Resource, props.defaultCostRateTableId, props.readonly, false);
        const resolvedField = resolveTimephasedFieldFromStore(props.fieldsStore, props.field) ?? props.field;
        const isLocked = isLockedField(resolvedField.settings?.format, resolvedField.name, props.user, props.tenant, props.entityType);

        if (isLocked || !isInlineEditable) {
            const inlineState = getTimephasedInlineEditState(props.field.name, props.item as Resource, props.defaultCostRateTableId, props.readonly, false);
            if (inlineState.shouldShowPanelHint && !isLocked) {
                const panelName = getTimephasedConfig(props.field.name)?.title ?? 'Timephased Data';
                return `${props.field.label} field has custom periods defined. To make changes, edit it in the ${panelName} panel from the Resource Dashboard.`;
            }

            return "This cell is read-only";
        }
    }

    return undefined;
}

const CellFormatter = <T extends IBaseEntity>(props: Props<IBaseEntity>) => {
    const onEditClick = React.useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        e.preventDefault();
        props.onEditClick();
    }, [props.withEditIcon]);

    const readonlyReason = isReadonlyCell(props);
    const isReadonly = !!readonlyReason;
    const isActionable = ViewService.isActionable(props.field);

    const readonlyIconTitle = React.useMemo(() => {
        if (!isReadonly || isActionable || props.openInPopup) {
            return undefined;
        }
        return readonlyReason ?? "This cell is read-only";
    }, [isReadonly, isActionable, props.openInPopup, readonlyReason]);

    const formattedControl = React.useMemo(() => {
        if (props.field.settings?.views?.list?.componentPath) {
            return React.createElement(
                ViewService.createListColumn<IListViewFieldColumn<T>>(props.field.settings.views.list.componentPath),
                {
                    entity: props.item,
                    field: props.field,
                    className: "font-14",
                    isTimelineView: props.isTimelineView,
                    entityType: props.entityType,
                    disableNavigation: props.disableNavigation
                }
            );
        }
        const Formatter = DisplayFieldService.getFieldFormatter(props.field, !props.disableNavigation, props.entityType, props.isTimelineView ? 'Timeline' : 'List');
        return Formatter ? <Formatter item={props.item} value={props.value} /> : undefined;
    }, [props.item, props.field, props.isTimelineView, props.entityType, props.disableNavigation, props.value]);

    return <div
        className="grid-editable-cell"
        id={props.controlId}
        data-selection-disabled
        aria-hidden={true}
        onClick={!isReadonly && !props.withEditIcon && !props.openInPopup ? onEditClick : undefined}
    >
        <div className="formatter">{formattedControl}</div>
        {!isReadonly && !!props.withEditIcon && !props.openInPopup
            && <div className="grid-edit-button">
                <IconButton iconProps={{ iconName: "Edit" }} onClick={onEditClick} />
            </div>}
        {!isActionable && isReadonly && !props.openInPopup
            && <span className="readonly-button">
                <Icon iconName="Uneditable2" title={readonlyIconTitle} />
            </span>}
        {!!props.openInPopup && <div className="grid-edit-button popup">
            <IconButton
                iconProps={{ iconName: isReadonly ? "View" : "Edit" }}
                onClick={onEditClick} />
        </div>}
    </div>;
}

function mapStateToProps(state: ApplicationState): StateProps {
    return {
        defaultCostRateTableId: getDefaultCostRateTable(state)?.id,
        user: state.user,
        tenant: state.tenant,
        fieldsStore: state.fields
    };
}

export default connect(mapStateToProps)(CellFormatter)